import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import type { IEstabelecimento, IImagem } from '@nomoh-monorepo/shared-types';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import {
  PRODUCT_TOUR_ON_WHATSAPP_TAB,
  TabsInkbarFixDirective,
} from '@nomoh-monorepo/shared-common-ui';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { ProfissionaisComponent } from '../components/profissionais/profissionais.component';
import { ClientesComponent } from '../components/clientes/clientes.component';
import { TipoServicoComponent } from '../components/tipo-servico/tipo-servico.component';
import { WhatsappConfigComponent } from '../components/whatsapp-config/whatsapp-config.component';
import { TranslateModule } from '@ngx-translate/core';
import { EditarEstabelecimentoModalComponent } from './editar-estabelecimento-modal/editar-estabelecimento-modal.component';

interface TabItem {
  id: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'lib-estabelecimentos',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TabsModule,
    TabsInkbarFixDirective,
    TooltipModule,
    TranslateModule,
    EditarEstabelecimentoModalComponent,
    ProfissionaisComponent,
    ClientesComponent,
    TipoServicoComponent,
    WhatsappConfigComponent,
  ],
  templateUrl: './estabelecimentos.component.html',
  styleUrl: './estabelecimentos.component.scss',
})
export class EstabelecimentosComponent {
  // Inject services
  private estabelecimentoService = inject(EstabelecimentoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private notificacaoService = inject(NotificacaoService);
  private readonly onWhatsappTabOpened = inject(PRODUCT_TOUR_ON_WHATSAPP_TAB, {
    optional: true,
  });

  // Signals
  activeTab = signal<string>('tipoServicos');
  modalEditarEstabelecimento = signal<boolean>(false);
  salvandoEstabelecimento = signal<boolean>(false);

  // Computed (acesso ao estabelecimento via DataService)
  estabelecimento = computed(() => {
    return this.estabelecimentoDataService.$estabelecimentoAtual() || null;
  });

  fotoCapa = computed(() => {
    const est = this.estabelecimentoDataService.$estabelecimentoAtual();
    if (!est || !est.imagens) return null;
    const foto = est.imagens.find((img: IImagem) => img.token === 'FOTO_CAPA');
    return foto ? `data:${foto.tipo};base64,${foto.conteudo}` : null;
  });

  fotoPerfil = computed(() => {
    const est = this.estabelecimentoDataService.$estabelecimentoAtual();
    if (!est || !est.imagens) return null;
    const foto = est.imagens.find(
      (img: IImagem) => img.token === 'FOTO_PERFIL'
    );
    return foto ? `data:${foto.tipo};base64,${foto.conteudo}` : null;
  });

  /** Força recriação das tags <img> no mobile quando imagens mudam */
  $bannerImagensVersao = computed(() => {
    const imagens =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.imagens;
    if (!imagens?.length) return '0';

    return imagens
      .map((img) => `${img.token}:${img.conteudo?.length ?? 0}`)
      .join('|');
  });

  // Configuração das abas
  tabs: TabItem[] = [
    { id: 'tipoServicos', label: 'Tipos de Serviços', icon: 'pi pi-list' },
    { id: 'profissionais', label: 'Profissionais', icon: 'pi pi-users' },
    { id: 'clientes', label: 'Clientes', icon: 'pi pi-address-book' },
    { id: 'configuracoes', label: 'Configurações', icon: 'pi pi-whatsapp' },
  ];

  readonly tourAnchorPorTab: Record<string, string> = {
    tipoServicos: 'config-tipos',
    profissionais: 'config-profissionais',
    clientes: 'config-clientes',
    configuracoes: 'config-whatsapp',
  };

  constructor() {
    effect(
      () => {
        const estabelecimento =
          this.estabelecimentoDataService.$estabelecimentoAtual();
        if (!estabelecimento) {
          this.carregarEstabelecimento();
        }
      }
    );
    void this.estabelecimentoService.getPlanoQuotas();
  }

  async carregarEstabelecimento() {
    try {
      // Por enquanto, buscar o primeiro estabelecimento disponível
      const estabelecimentos = await this.estabelecimentoService.findAll();
      if (estabelecimentos.length > 0) {
        await this.estabelecimentoService.findById(estabelecimentos[0].id);
      }
    } catch {
      this.notificacaoService.notificacaoError(
        'Erro ao carregar estabelecimento'
      );
    }
  }

  onTabChange(tabId: string) {
    this.activeTab.set(tabId);
    if (tabId === 'configuracoes') {
      this.onWhatsappTabOpened?.();
    }
  }

  abrirModalEdicao() {
    this.modalEditarEstabelecimento.set(true);
  }

  fecharModalEdicao() {
    this.modalEditarEstabelecimento.set(false);
  }

  async salvarEstabelecimento(estabelecimento: IEstabelecimento) {
    this.salvandoEstabelecimento.set(true);
    try {
      if (estabelecimento.id) {
        await this.estabelecimentoService.atualizar(
          estabelecimento.id,
          estabelecimento
        );
      } else {
        await this.estabelecimentoService.criar(estabelecimento);
      }

      // Recarregar estabelecimento
      await this.carregarEstabelecimento();
      this.fecharModalEdicao();
    } catch (error) {
      this.notificacaoService.notificacaoError(
        'Erro ao salvar estabelecimento'
      );
      console.error(error);
    } finally {
      this.salvandoEstabelecimento.set(false);
    }
  }

  formatarTelefone(telefone: string): string {
    return formatarTelefone(telefone);
  }
}
